import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EntitiesListRowProps, EntitiesListItem } from '../EntitiesListRow/EntitiesListRow';
import './entities-list.scss';
import { BaseEntityNameLess } from '@api/entities/BaseEntity';
import { LoadingBackdrop } from '@components/LoadingBackdrop/LoadingBackdrop';
import { closestCenter, DndContext, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import Button from '@components/buttons/Button';
import type { DragEndEvent } from '@dnd-kit/core/dist/types';
import {
    ActionDialogProps,
    ActionMenuProps,
    MenuActionType,
} from '@components/ActionMenu/ActionMenu';

export type EntitiesListProps<T extends BaseEntityNameLess> = {
    loading?: boolean;
    entities: T[];
    selectedId?: string;
    menuActions?: (Pick<MenuActionType, 'label' | 'dialogTitle'> & {
        dialogContentFabric?: (args: ActionMenuProps) => React.FC<ActionDialogProps>;
    })[];
    loadDataFn: () => Promise<void>;
    draggable?: boolean;
    showNumbering?: boolean;
    onUpdateListOrderFn?: (entities: T[]) => Promise<void>;
} & Omit<EntitiesListRowProps<T>, 'entity' | 'selected'>;

const EntitiesList = <T extends BaseEntityNameLess>({
    loading = false,
    entities,
    form,
    onRowClick,
    deleteFn,
    getUrlFunc,
    selectedId,
    hasLink,
    menuActions,
    loadDataFn,
    draggable,
    showNumbering = false,
    onUpdateListOrderFn,
}: EntitiesListProps<T>) => {
    const [showButtons, setShowButtons] = useState<boolean>(false);
    const [items, setItems] = useState(entities);

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 10,
            },
        })
    );
    const { t } = useTranslation();

    useEffect(() => {
        if (entities.length && items.length) {
            const initialOrder = entities.every(({ id }, ind) => {
                return items.length > ind ? id === items[ind].id : false;
            });
            setShowButtons(!initialOrder);
        }
    }, [items, entities]);

    useEffect(() => {
        setItems(entities);
    }, [entities]);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setItems(items => {
                let oldIndex = 0,
                    newIndex = 0;
                items.forEach(({ id }, ind) => {
                    if (id === active.id) {
                        oldIndex = ind;
                    }
                    if (id === over.id) {
                        newIndex = ind;
                    }
                });

                return arrayMove(items, oldIndex, newIndex);
            });
        }
    }, []);

    // const refs = useMemo(() => entities.reduce((acc, entity) => {
    //     if (entity['id']) {
    //         acc[entity.id] = React.createRef();
    //     }
    //     return acc;
    // }, {}), [entities]);

    // useEffect(() => {
    //     if (selectedId && refs[selectedId].current) {
    //         refs[selectedId].current.scrollIntoView({
    //             behavior: 'smooth',
    //             block: 'start',
    //         });
    //     }
    // }, [selectedId]);

    const revertListOrderHandler = useCallback(() => {
        setItems(entities);
    }, [entities]);

    const updateListOrderHandler = useCallback(async () => {
        await onUpdateListOrderFn?.(items);
        setShowButtons(false);
    }, [items, onUpdateListOrderFn]);

    return (
        <div className="entities-list" data-testid="entitiesList">
            <LoadingBackdrop isVisible={loading} transparent>
                <DndContext
                    sensors={sensors}
                    modifiers={[restrictToVerticalAxis]}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext items={items} strategy={verticalListSortingStrategy}>
                        {items?.map((entity, ind) => (
                            <EntitiesListItem
                                key={entity.id}
                                entity={entity}
                                rowNumber={showNumbering ? ++ind : null}
                                deleteFn={deleteFn}
                                onRowClick={onRowClick as any}
                                form={form}
                                selected={entity.id === selectedId}
                                // ref={refs[entity.id]}
                                getUrlFunc={getUrlFunc as any}
                                hasLink={hasLink}
                                draggable={draggable}
                                menuActions={menuActions?.map(action => ({
                                    ...action,
                                    ...(action.dialogContentFabric
                                        ? {
                                              dialogContent: action?.dialogContentFabric({
                                                  id: entity.id,
                                                  loadEntitiesFn: loadDataFn,
                                                  deleteEntityFn: deleteFn,
                                              }),
                                          }
                                        : {}),
                                }))}
                                loadDataFn={loadDataFn}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
                {showButtons ? (
                    <div className="entities-list-order-buttons">
                        <Button
                            onClick={revertListOrderHandler}
                            text={t('entities-list.revert-btn')}
                            colorType="transparent"
                            testId="revertEntitiesOrderChangeBtn"
                        />
                        <Button
                            type="submit"
                            onClick={updateListOrderHandler}
                            text={t('entities-list.update-btn')}
                            colorType="dark"
                            testId="confirmEntitiesOrderChangeBtn"
                        />
                    </div>
                ) : null}
            </LoadingBackdrop>
        </div>
    );
};

export default EntitiesList;
